import { WorkerRequirementStatus } from "@clipboard-health/contract-worker-app-bff";
import { isDefined } from "@clipboard-health/util-ts";
import { Skeleton } from "@mui/material";
import { APP_V2_APP_EVENTS, logError } from "@src/appV2/lib/analytics";
import { useGetCourseEnrollments } from "@src/appV2/redesign/Accounts/Courses/api/useGetCourseEnrollments";
import { COURSE_PLAYER_FULL_PATH } from "@src/appV2/redesign/Accounts/Courses/paths";
import { useGetScormCourseConfig } from "@src/appV2/redesign/Accounts/Courses/useScormCourseConfig";
import { useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";

import { Button } from "../../../../components/Button";
import { PageFooterWrapper } from "../../../../components/PageFooterWrapper";
import { PageWrapper } from "../../../../components/PageWrapper";
import { StickyScrollPageHeader } from "../../../../components/StickyScrollPageHeader";
import { DocumentUploadFooter } from "../../Footer";
import { DocumentDetailsInstructions } from "../../Instructions";
import { DocumentDetailsMetadata } from "../../Metadata";
import { DocumentDetailsRequiredBySection } from "../../RequiredBySection/Section";
import { DocumentPageScrollWrapper } from "../../ScrollWrapper";
import { DocumentDetailsStatusCard } from "../../StatusCard";
import { useDocumentDetailsActions } from "../../useDocumentDetailsActions";
import { useRequirementDetailsContext } from "../../useRequirementDetailsContext";
import { useRequirementPrerequisiteActions } from "../../useRequirementPrerequisiteActions";

const SUPPORTED_SCORM_VERSION = "1.2";

export function DocumentDetailsUploadPage() {
  const { requirement: workerRequirement } = useRequirementDetailsContext();
  const scrollRef = useRef<HTMLDivElement>(null);
  const { requirementId, status, requirement } = workerRequirement;
  const { name, disableWorkerUpload } = requirement?.attributes ?? {};

  const { uploadDocument, deleteDocument } = useDocumentDetailsActions();
  const { withPrerequisiteCheck, prerequisiteBottomSheet } = useRequirementPrerequisiteActions();

  const history = useHistory();
  const { getScormConfig } = useGetScormCourseConfig();
  const scormConfig = getScormConfig(requirementId);
  const isScormCourse = isDefined(scormConfig);
  const isSupportedScormCourse =
    isScormCourse && scormConfig.scormVersion === SUPPORTED_SCORM_VERSION;

  useEffect(() => {
    if (isScormCourse && !isSupportedScormCourse) {
      logError(APP_V2_APP_EVENTS.SCORM_UNSUPPORTED_VERSION, {
        error: new Error(
          `Unsupported SCORM version: ${scormConfig.scormVersion}. Only 1.2 is supported.`
        ),
        metadata: { requirementId, courseId: scormConfig.courseId },
      });
    }
  }, [isScormCourse, isSupportedScormCourse, requirementId, scormConfig]);

  const { data: enrollments, isLoading: isLoadingEnrollments } = useGetCourseEnrollments(
    requirementId,
    {
      enabled: isSupportedScormCourse,
    }
  );
  const hasStarted =
    enrollments?.some((enrollment) => enrollment.attributes.status === "started") ?? false;

  return (
    <PageWrapper variant="tertiary">
      <StickyScrollPageHeader
        shouldWrapTitle
        title={name ?? ""}
        headerVariant="tertiary"
        scrollContainerRef={scrollRef}
      />

      <DocumentPageScrollWrapper ref={scrollRef}>
        <DocumentDetailsMetadata />
        <DocumentDetailsStatusCard />
        <DocumentDetailsInstructions />
        <DocumentDetailsRequiredBySection />
      </DocumentPageScrollWrapper>

      {isSupportedScormCourse && status !== WorkerRequirementStatus.APPROVED ? (
        <PageFooterWrapper>
          {isLoadingEnrollments ? (
            <Skeleton
              variant="rounded"
              width="100%"
              sx={(theme) => ({ height: theme.size?.button.large.height })}
            />
          ) : (
            <Button
              fullWidth
              size="large"
              variant="contained"
              onClick={withPrerequisiteCheck(() => {
                history.push(`${COURSE_PLAYER_FULL_PATH}/${requirementId}`);
              })}
            >
              {hasStarted ? "Complete MedTrainer Course" : "Start MedTrainer Course"}
            </Button>
          )}
        </PageFooterWrapper>
      ) : (
        !isScormCourse &&
        disableWorkerUpload !== true && (
          <DocumentUploadFooter
            status={status}
            resubmissionDisabledFor={requirement?.attributes?.resubmissionDisabledFor}
            onUploadButtonClick={withPrerequisiteCheck(uploadDocument)}
            onDeleteButtonClick={deleteDocument}
          />
        )
      )}
      {prerequisiteBottomSheet}
    </PageWrapper>
  );
}
